import * as React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faPiggyBank, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import CashCollectionForm from 'src/features/cashCollections/components/forms/CashCollectionForm';
import {
    CASH_COLLECTION_RESOURCE,
    createCashCollectionFormSchema,
    type CreateCashCollectionFormSchemaValues,
    useCreateCashCollection,
} from 'src/features/cashCollections/hooks/useCreateCashCollection';
import { cashCollectionsMessages } from 'src/features/cashCollections/utils/messages';
import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { type DataTableNewEntryProps, tableMessages } from 'src/registry/components/table';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { cn } from 'src/utils';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

interface NewCashCollectionProps extends DataTableNewEntryProps<any> {}
function NewCashCollection({ className }: NewCashCollectionProps) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const { toast } = useToast();

    const { mutateAsync, isPending } = useCreateCashCollection({
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [CASH_COLLECTION_RESOURCE],
            });
            setIsModalOpen(false);
            form.reset();
            toast({
                title: formatMessage(cashCollectionsMessages.cashCollectionsCreated),
                description: formatMessage(cashCollectionsMessages.cashCollectionCreatedDescription),
            });
        },
        onError: (e) => {
            const error = Object.entries(e?.response?.data || {})[0];
            const errorCode = e?.response?.data.code;
            const errorCodeConfiguration = 'parking_not_configured_for_cash_collection';

            const errorMessage =
                errorCode == errorCodeConfiguration
                    ? formatMessage(cashCollectionsMessages.cashCollectionConfigurationError)
                    : error
                      ? `${error[0]}, ${error[1][0]}`
                      : formatMessage(generalErrorMessages.somethingWentWrongRetry);

            toast({
                variant: 'destructive',
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: errorMessage,
            });
        },
    });

    const getDefaultValues = () => ({
        collected_by: undefined,
        parking: undefined,
        paystation: undefined,
        status: undefined,
        type: undefined,
        nominal_amounts: {},
        description: undefined,
        files: undefined,
        date: new Date().toISOString(),
        time: `${String(new Date().getHours()).padStart(2, '0')}:${String(new Date().getMinutes()).padStart(2, '0')}`,
    });

    const form = useForm<CreateCashCollectionFormSchemaValues>({
        resolver: zodResolver(createCashCollectionFormSchema),
        defaultValues: getDefaultValues(),
    });

    const onSubmit: SubmitHandler<CreateCashCollectionFormSchemaValues> = async (values) => {
        const formData = new FormData();

        formData.append('collected_by', values.collected_by);
        formData.append('parking', values.parking);
        formData.append('paystation', values.paystation);

        if (form.watch('type') === 'collect' && values.status) {
            formData.append('status', values.status);
        }

        formData.append('type', values.type);
        formData.append('description', values.description ?? '');

        const date = values.date ? new Date(values.date) : new Date();
        const [hours, minutes] = values.time?.split(':').map(Number) ?? [0, 0];
        date.setHours(hours, minutes, 0);
        formData.append('date', date.toISOString());

        const denormalizeKey = (key: string) => key.replace(/_/g, '.');
        const fixedNominals = Object.fromEntries(
            Object.entries(values.nominal_amounts ?? {}).map(([k, v]) => [denormalizeKey(k), v]),
        );
        formData.append('nominal_amounts', JSON.stringify(fixedNominals));

        if (values.files) {
            values.files.forEach((file) => {
                formData.append('files', file);
            });
        }

        await mutateAsync(formData);
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset(getDefaultValues());
        }
        setIsModalOpen(isOpen);
        if (isOpen) {
            form.reset(getDefaultValues());
        }
    };

    return (
        <Dialog open={isModalOpen} onOpenChange={onOpenChange} modal>
            <DialogTrigger asChild>
                <Button variant="outline" className={cn('ml-auto cursor-pointer', className)}>
                    <FontAwesomeIcon className="text-primary-700" icon={faPlus} />
                    {formatMessage(tableMessages.new, {
                        entry: formatMessage(cashCollectionsMessages.cashCollection).toLowerCase(),
                    })}
                </Button>
            </DialogTrigger>

            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-4">
                        <SquareIcon icon={faPiggyBank} />
                        <div>
                            <DialogTitle>
                                {formatMessage(tableMessages.new, {
                                    entry: formatMessage(cashCollectionsMessages.cashCollection).toLowerCase(),
                                })}
                            </DialogTitle>
                            <DialogDescription>
                                {formatMessage(cashCollectionsMessages.newCashCollectionDescription)}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogSeparator />

                <DialogBody>
                    <CashCollectionForm form={form} onSubmit={form.handleSubmit(onSubmit)} isEditModal={false} />
                </DialogBody>

                <DialogSeparator />

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>

                    <Button type="submit" onClick={form.handleSubmit(onSubmit)} className="flex-1">
                        {isPending && <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />}
                        {formatMessage(cashCollectionsMessages.createNewCashCollection)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { NewCashCollection };
